import { Application, Container, Graphics, Point, Sprite, Texture } from 'pixi.js';
import { GridObject, GridSystem } from './GridSystem';
import { WallDirection, WallSegment, WallSystem } from './WallSystem';
import { FurnitureSystem, FurnitureRotation } from './FurnitureSystem';
import { DoorSystem } from './DoorSystem';
import { IsometricCamera } from '@rendering/IsometricCamera';
import { Layer, LayerSystem } from '@rendering/LayerSystem';
import { RoomCategory } from '@gameplay/management/rooms/RoomTypes';
import { RoomLegend } from '@ui/RoomLegend';

export type PlacementMode = 'floor' | 'wall' | 'door' | 'furniture' | 'room_category' | 'room_manager';
export type WallBuildMode = 'rectangle' | 'single_line';

export interface WallEdge {
  tile: Point; // Tile within the selection bounds
  neighbor: Point; // Adjacent grid position across the edge
  direction: WallDirection;
}

export interface PlacementResult {
  mode: PlacementMode;
  floors: GridObject[];
  walls: WallSegment[];
  furniture: GridObject[];
  rejectedTiles: Point[];
  rejectedWalls: WallEdge[];
  source?: 'normal' | 'undo' | 'redo';
}

export class PlacementSystem {
  private readonly app: Application;
  private readonly gridSystem: GridSystem;
  private readonly layerSystem: LayerSystem;
  private readonly camera: IsometricCamera;
  private readonly wallSystem: WallSystem;
  private readonly furnitureSystem: FurnitureSystem;
  private readonly doorSystem: DoorSystem;
  private readonly previewLayer: Container;

  private floorPreviewTextureValid: Texture;
  private floorPreviewTextureInvalid: Texture;
  private roomCategoryPreviewTexture: Texture;

  private placementMode: PlacementMode = 'floor';
  private wallBuildMode: WallBuildMode = 'rectangle';
  private isDrawing = false;
  private dragStart: Point | null = null;
  private currentFloor = 0;
  private selectedFurnitureId: string | null = null;
  private furnitureRotation: FurnitureRotation = 0;
  private doorDirection: 'above' | 'below' | 'left' | 'right' = 'above';
  private lastPreviewGridPoint: Point | null = null; // Track last preview position
  private currentRoomCategory: RoomCategory | null = null;

  constructor(
    app: Application,
    gridSystem: GridSystem,
    wallSystem: WallSystem,
    furnitureSystem: FurnitureSystem,
    doorSystem: DoorSystem,
    layerSystem: LayerSystem,
    camera: IsometricCamera
  ) {
    this.app = app;
    this.gridSystem = gridSystem;
    this.wallSystem = wallSystem;
    this.furnitureSystem = furnitureSystem;
    this.doorSystem = doorSystem;
    this.wallSystem = wallSystem;
    this.layerSystem = layerSystem;
    this.camera = camera;

    this.previewLayer = new Container();
    this.previewLayer.alpha = 0.55;
    this.camera.container.addChild(this.previewLayer);

    this.floorPreviewTextureValid = this.createFloorPreviewTexture(0x7ad37a, 0.7);
    this.floorPreviewTextureInvalid = this.createFloorPreviewTexture(0xd37a7a, 0.7);
    this.roomCategoryPreviewTexture = this.createRoomCategoryPreviewTexture();
  }

  public setMode(mode: PlacementMode): void {
    this.clearPreview(); // Clear any existing preview when switching modes
    this.lastPreviewGridPoint = null; // Reset preview tracking
    this.placementMode = mode;
    // Reset furniture rotation when switching modes
    if (mode !== 'furniture') {
      this.furnitureRotation = 0;
    }
    // Reset door direction when switching modes
    if (mode !== 'door') {
      this.doorDirection = 'above';
    }
  }

  public getMode(): PlacementMode {
    return this.placementMode;
  }

  public setSelectedFurniture(furnitureId: string | null): void {
    this.clearPreview(); // Clear existing preview when changing furniture
    this.lastPreviewGridPoint = null; // Reset preview tracking
    this.selectedFurnitureId = furnitureId;
    // Reset rotation when selecting new furniture
    this.furnitureRotation = 0;
  }

  public getSelectedFurniture(): string | null {
    return this.selectedFurnitureId;
  }

  public rotateFurniture(): void {
    if (this.placementMode === 'furniture' && this.selectedFurnitureId) {
      // Cycle through rotations: 0 -> 90 -> 180 -> 270 -> 0
      this.furnitureRotation = ((this.furnitureRotation + 90) % 360) as FurnitureRotation;
      
      // Update preview with new rotation if we're hovering
      if (this.lastPreviewGridPoint) {
        this.renderFurniturePreview(this.lastPreviewGridPoint);
      }
    } else if (this.placementMode === 'door') {
      // Cycle through door directions: above -> right -> below -> left -> above
      const directions: ('above' | 'below' | 'left' | 'right')[] = ['above', 'right', 'below', 'left'];
      const currentIndex = directions.indexOf(this.doorDirection);
      this.doorDirection = directions[(currentIndex + 1) % directions.length];
      
      // Update preview with new direction if we're hovering
      if (this.lastPreviewGridPoint) {
        this.renderDoorPreview(this.lastPreviewGridPoint);
      }
    }
  }

  public setRoomCategory(category: RoomCategory | null): void {
    this.currentRoomCategory = category;
    this.clearPreview(); // Clear existing preview when changing category
    this.lastPreviewGridPoint = null;
  }

  public getRoomCategory(): RoomCategory | null {
    return this.currentRoomCategory;
  }

  public getFurnitureRotation(): FurnitureRotation {
    return this.furnitureRotation;
  }

  public setFurnitureRotation(rotation: FurnitureRotation): void {
    this.furnitureRotation = rotation;
  }

  public resetFurnitureRotation(): void {
    this.furnitureRotation = 0;
  }

  public getWallBuildMode(): WallBuildMode {
    return this.wallBuildMode;
  }

  public setWallBuildMode(mode: WallBuildMode): void {
    this.wallBuildMode = mode;
    this.clearPreview(); // Clear existing preview when switching modes
    this.lastPreviewGridPoint = null;
  }

  public toggleWallBuildMode(): WallBuildMode {
    this.wallBuildMode = this.wallBuildMode === 'rectangle' ? 'single_line' : 'rectangle';
    this.clearPreview(); // Clear existing preview when switching modes
    this.lastPreviewGridPoint = null;
    return this.wallBuildMode;
  }

  public removeFloorAt(gridPoint: Point, floor = this.currentFloor): GridObject | null {
    const existing = this.gridSystem.getObject(gridPoint.x, gridPoint.y, floor);
    if (!existing || existing.type !== 'floor') {
      return null;
    }

    const removed = this.gridSystem.removeObject(gridPoint.x, gridPoint.y, floor);
    if (!removed) {
      return null;
    }

    if (removed.sprite) {
      removed.sprite.parent?.removeChild(removed.sprite);
      removed.sprite.destroy();
      removed.sprite = undefined;
    }

    return removed;
  }

  public removeWallAt(gridPoint: Point, worldPoint: Point, floor = this.currentFloor): WallSegment | null {
    const candidates = this.collectWallCandidates(gridPoint, floor);
    if (candidates.length === 0) {
      return null;
    }

    let best = candidates[0];
    let bestDistance = this.pointSegmentDistance(worldPoint, best.worldStart, best.worldEnd);

    for (let i = 1; i < candidates.length; i += 1) {
      const candidate = candidates[i];
      const distance = this.pointSegmentDistance(worldPoint, candidate.worldStart, candidate.worldEnd);
      if (distance < bestDistance) {
        best = candidate;
        bestDistance = distance;
      }
    }

    // Require pointer to be reasonably close to the wall to avoid accidental deletions.
    const maxDistance = 32; // pixels in world space
    if (bestDistance > maxDistance) {
      return null;
    }

    const removed = this.wallSystem.removeSegment(
      best.tile.x,
      best.tile.y,
      best.neighbor.x,
      best.neighbor.y,
      floor
    );

    if (!removed) {
      return null;
    }

    if (removed.graphic) {
      removed.graphic.parent?.removeChild(removed.graphic);
      removed.graphic.destroy();
      removed.graphic = undefined;
    }

    return removed;
  }

  public removeFurnitureAt(gridPoint: Point, floor = this.currentFloor): GridObject | null {
    const furniture = this.furnitureSystem.getFurnitureAt(gridPoint.x, gridPoint.y, floor);
    if (!furniture) {
      return null;
    }

    return this.furnitureSystem.removeFurniture(furniture.gridX, furniture.gridY, floor);
  }

  public placeFurnitureAt(gridPoint: Point, floor = this.currentFloor): GridObject | null {
    if (!this.selectedFurnitureId) {
      return null;
    }

    return this.furnitureSystem.placeFurniture(
      this.selectedFurnitureId,
      gridPoint.x,
      gridPoint.y,
      floor,
      this.furnitureRotation
    );
  }

  public placeDoorAt(gridPoint: Point): boolean {
    // Check for walls on all 4 edges around this grid position
    const x = gridPoint.x;
    const y = gridPoint.y;
    
    const availableDirections = this.getAvailableWallDirections(x, y);
    
    if (availableDirections.length === 0) {
      console.warn('Cannot place door: no adjacent walls found at', gridPoint);
      return false;
    }
    
    // If the current direction isn't available, use the first available one
    let direction = this.doorDirection;
    if (!availableDirections.includes(direction)) {
      direction = availableDirections[0];
    }
    
    // Place door on the selected direction
    let doorCoords: { startX: number, startY: number, endX: number, endY: number };
    
    switch (direction) {
      case 'above':
        doorCoords = { startX: x, startY: y + 1, endX: x + 1, endY: y + 1 };
        break;
      case 'below':
        doorCoords = { startX: x, startY: y, endX: x + 1, endY: y };
        break;
      case 'left':
        doorCoords = { startX: x, startY: y, endX: x, endY: y + 1 };
        break;
      case 'right':
        doorCoords = { startX: x + 1, startY: y, endX: x + 1, endY: y + 1 };
        break;
    }
    
    const door = this.doorSystem.addDoor(doorCoords.startX, doorCoords.startY, doorCoords.endX, doorCoords.endY);
    console.log(`Placed door ${direction} at:`, doorCoords.startX, doorCoords.startY, doorCoords.endX, doorCoords.endY);
    return door !== null;
  }

  private getAvailableWallDirections(x: number, y: number): ('above' | 'below' | 'left' | 'right')[] {
    const directions: ('above' | 'below' | 'left' | 'right')[] = [];
    
    // Check for walls at integer boundaries around this grid position
    if (this.wallSystem.isOccupied(x, y + 1, x + 1, y + 1)) {
      directions.push('above');
    }
    if (this.wallSystem.isOccupied(x, y, x + 1, y)) {
      directions.push('below');
    }
    if (this.wallSystem.isOccupied(x, y, x, y + 1)) {
      directions.push('left');
    }
    if (this.wallSystem.isOccupied(x + 1, y, x + 1, y + 1)) {
      directions.push('right');
    }
    
    return directions;
  }

  public restoreWallSegment(segment: WallSegment): WallSegment {
    const restored = this.wallSystem.addSegment(
      segment.start.x,
      segment.start.y,
      segment.end.x,
      segment.end.y,
      segment.floor
    );

    const edge = this.segmentToEdge(restored);
    const { start, end } = this.computeWallWorldEdge(edge);
    const wall = new Graphics();
    wall.lineStyle(8, 0xc9c3b5, 1);
    wall.moveTo(0, 0);
    wall.lineTo(end.x - start.x, end.y - start.y);
    wall.position.copyFrom(start);

    const depthPoint = new Point(
      Math.max(edge.tile.x, edge.neighbor.x),
      Math.max(edge.tile.y, edge.neighbor.y)
    );

    this.layerSystem.add(wall, 'Walls', depthPoint);
    restored.graphic = wall;
    return restored;
  }

  public beginDrag(gridPoint: Point): void {
    if (this.placementMode === 'furniture') {
      // Furniture uses single-click placement, not drag
      this.isDrawing = false;
      this.dragStart = null;
      return;
    }

    this.isDrawing = true;
    this.dragStart = gridPoint.clone();
    this.clearPreview();
  }

  public updateDrag(gridPoint: Point): void {
    // Handle door preview (single-click, no drag - similar to furniture)
    if (this.placementMode === 'door') {
      // Only update door preview if position changed
      if (!this.lastPreviewGridPoint || 
          this.lastPreviewGridPoint.x !== gridPoint.x || 
          this.lastPreviewGridPoint.y !== gridPoint.y) {
        this.lastPreviewGridPoint = gridPoint.clone();
        this.renderDoorPreview(gridPoint);
      }
      return;
    }

    if (this.placementMode === 'furniture') {
      // Only update furniture preview if position changed and furniture is selected
      if (this.selectedFurnitureId && 
          (!this.lastPreviewGridPoint || 
           this.lastPreviewGridPoint.x !== gridPoint.x || 
           this.lastPreviewGridPoint.y !== gridPoint.y)) {
        this.lastPreviewGridPoint = gridPoint.clone();
        this.renderFurniturePreview(gridPoint);
      } else if (!this.selectedFurnitureId) {
        this.clearPreview(); // Clear preview if no furniture is selected
        this.lastPreviewGridPoint = null;
      }
      return;
    }

    // Clear furniture preview tracking when not in furniture mode
    this.lastPreviewGridPoint = null;

    if (!this.isDrawing || !this.dragStart) {
      return;
    }

    this.renderPreview(this.dragStart, gridPoint);
  }

  public endDrag(gridPoint: Point): PlacementResult | null {
    // Handle door placement (single-click, no drag)
    if (this.placementMode === 'door') {
      const door = this.placeDoorAt(gridPoint);
      this.clearPreview();
      return {
        mode: 'door',
        floors: [],
        walls: [],
        furniture: [],
        rejectedTiles: door ? [] : [gridPoint],
        rejectedWalls: []
      };
    }

    // Handle furniture placement (single-click, no drag)
    if (this.placementMode === 'furniture') {
      const furniture = this.placeFurnitureAt(gridPoint);
      this.clearPreview();
      return {
        mode: 'furniture',
        floors: [],
        walls: [],
        furniture: furniture ? [furniture] : [],
        rejectedTiles: furniture ? [] : [gridPoint],
        rejectedWalls: []
      };
    }

    // Handle room category painting 
    if (this.placementMode === 'room_category') {
      if (!this.isDrawing || !this.dragStart) {
        return null;
      }
      
      const start = this.dragStart;
      this.isDrawing = false;
      this.dragStart = null;
      
      // Get tiles with existing floors only (same logic as floor evaluation)
      const { validTiles } = this.evaluateRoomCategoryArea(start, gridPoint);
      // Return floors to indicate which tiles to categorize
      const floors = validTiles.map((tile: Point) => ({
        id: `room_category_tile_${tile.x}_${tile.y}`,
        type: 'floor' as const,
        gridX: tile.x,
        gridY: tile.y,
        floor: this.currentFloor,
        width: 1,
        height: 1
      }));
      
      this.clearPreview();
      return {
        mode: 'room_category',
        floors,
        walls: [],
        furniture: [],
        rejectedTiles: [],
        rejectedWalls: []
      };
    }

    if (!this.isDrawing || !this.dragStart) {
      return null;
    }

    const start = this.dragStart;
    this.isDrawing = false;
    this.dragStart = null;

    if (this.placementMode === 'floor') {
      const { validTiles, invalidTiles } = this.evaluateFloorArea(start, gridPoint);
      const floors = validTiles.map((tile) => this.placeFloor(tile));
      this.clearPreview();
      return {
        mode: 'floor',
        floors,
        walls: [],
        furniture: [],
        rejectedTiles: invalidTiles,
        rejectedWalls: []
      };
    }

    if (this.placementMode === 'wall') {
      if (this.wallBuildMode === 'single_line') {
        const wall = this.placeSingleLineWall(start, gridPoint);
        this.clearPreview();
        return {
          mode: 'wall',
          floors: [],
          walls: wall ? [wall] : [],
          furniture: [],
          rejectedTiles: [],
          rejectedWalls: wall ? [] : [{ tile: start, neighbor: gridPoint, direction: 'horizontal' }]
        };
      } else {
        const { validEdges, invalidEdges } = this.evaluateWallEdges(start, gridPoint);
        const walls = validEdges.map((edge) => this.placeWall(edge));
        this.clearPreview();
        return {
          mode: 'wall',
          floors: [],
          walls,
          furniture: [],
          rejectedTiles: [],
          rejectedWalls: invalidEdges
        };
      }
    }

    // Fallback for any other modes
    this.clearPreview();
    return null;
  }

  public cancelDrag(): void {
    this.isDrawing = false;
    this.dragStart = null;
    this.clearPreview();
  }

  private renderPreview(start: Point, end: Point): void {
    this.clearPreview();

    if (this.placementMode === 'floor') {
      this.renderFloorPreview(start, end);
    } else if (this.placementMode === 'room_category') {
      this.renderRoomCategoryPreview(start, end);
    } else if (this.placementMode === 'furniture') {
      this.renderFurniturePreview(end);
    } else {
      this.renderWallPreview(start, end);
    }
  }

  private renderFloorPreview(start: Point, end: Point): void {
    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    const minY = Math.min(start.y, end.y);
    const maxY = Math.max(start.y, end.y);

    for (let x = minX; x <= maxX; x += 1) {
      for (let y = minY; y <= maxY; y += 1) {
        const worldPoint = this.gridSystem.gridToWorld(x, y);
        const isValid = !this.gridSystem.isOccupied(x, y, this.currentFloor);
        const sprite = new Sprite(
          isValid ? this.floorPreviewTextureValid : this.floorPreviewTextureInvalid
        );
        sprite.anchor.set(0.5, 1);
        sprite.position.copyFrom(worldPoint);
        this.previewLayer.addChild(sprite);
      }
    }
  }

  private renderRoomCategoryPreview(start: Point, end: Point): void {
    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    const minY = Math.min(start.y, end.y);
    const maxY = Math.max(start.y, end.y);

    const categoryColor = RoomLegend.getRoomColor(this.currentRoomCategory);

    for (let x = minX; x <= maxX; x += 1) {
      for (let y = minY; y <= maxY; y += 1) {
        // Only show preview on existing floor tiles
        if (this.gridSystem.hasFloor(x, y, this.currentFloor)) {
          const worldPoint = this.gridSystem.gridToWorld(x, y);
          const sprite = new Sprite(this.roomCategoryPreviewTexture);
          sprite.anchor.set(0.5, 1);
          sprite.position.copyFrom(worldPoint);
          sprite.tint = categoryColor;
          sprite.alpha = 0.7;
          this.previewLayer.addChild(sprite);
        }
      }
    }
  }

  private renderWallPreview(start: Point, end: Point): void {
    if (this.wallBuildMode === 'single_line') {
      this.renderSingleLineWallPreview(start, end);
    } else {
      this.renderRectangleWallPreview(start, end);
    }
  }

  private renderRectangleWallPreview(start: Point, end: Point): void {
    const { validEdges, invalidEdges } = this.evaluateWallEdges(start, end);

    validEdges.forEach((edge) => this.addWallPreview(edge, true));
    invalidEdges.forEach((edge) => this.addWallPreview(edge, false));
  }

  private renderSingleLineWallPreview(start: Point, end: Point): void {
    // For single line mode, constrain to cardinal directions only
    const constrainedEnd = this.constrainToCardinalDirection(start, end);
    
    // Draw a direct line between start and constrained end points
    const startWorld = this.gridSystem.gridToWorld(start.x, start.y);
    const endWorld = this.gridSystem.gridToWorld(constrainedEnd.x, constrainedEnd.y);
    
    // Create a preview line
    const preview = new Graphics();
    preview.lineStyle(6, 0x7ad37a, 0.9); // Green for valid
    preview.moveTo(0, 0);
    preview.lineTo(endWorld.x - startWorld.x, endWorld.y - startWorld.y);
    preview.position.copyFrom(startWorld);
    this.previewLayer.addChild(preview);
  }

  private renderFurniturePreview(gridPoint: Point): void {
    // Always clear existing preview first
    this.clearPreview();

    if (!this.selectedFurnitureId) {
      return;
    }

    const definition = this.furnitureSystem.getFurnitureDefinition(this.selectedFurnitureId);
    if (!definition) {
      return;
    }

    // Check if the furniture can be placed at this position with current rotation
    const canPlace = this.furnitureSystem.canPlaceFurniture(
      this.selectedFurnitureId,
      gridPoint.x,
      gridPoint.y,
      this.currentFloor,
      this.furnitureRotation
    );

    // Get rotated dimensions
    const { width, height } = this.getRotatedDimensions(definition, this.furnitureRotation);

    // Show preview for each tile the furniture will occupy
    for (let dx = 0; dx < width; dx++) {
      for (let dy = 0; dy < height; dy++) {
        const tileX = gridPoint.x + dx;
        const tileY = gridPoint.y + dy;
        const worldPoint = this.gridSystem.gridToWorld(tileX, tileY);

        // Use different colors for valid vs invalid placement
        const sprite = new Sprite(
          canPlace ? this.floorPreviewTextureValid : this.floorPreviewTextureInvalid
        );
        sprite.anchor.set(0.5, 1);
        sprite.position.copyFrom(worldPoint);
        this.previewLayer.addChild(sprite);
      }
    }
  }

  private renderDoorPreview(gridPoint: Point): void {
    // Always clear existing preview first
    this.clearPreview();

    const x = gridPoint.x;
    const y = gridPoint.y;
    
    const availableDirections = this.getAvailableWallDirections(x, y);
    
    if (availableDirections.length === 0) {
      // No walls available - show red preview
      const worldPoint = this.gridSystem.gridToWorld(x, y);
      const sprite = new Sprite(this.floorPreviewTextureInvalid);
      sprite.anchor.set(0.5, 1);
      sprite.position.copyFrom(worldPoint);
      sprite.tint = 0x8B4513; // Brown color for door preview
      this.previewLayer.addChild(sprite);
      return;
    }
    
    // If current direction isn't available, show the first available one
    let direction = this.doorDirection;
    if (!availableDirections.includes(direction)) {
      direction = availableDirections[0];
    }
    
    // Get the door coordinates that match exactly with wall detection
    let doorCoords: { startX: number, startY: number, endX: number, endY: number };
    
    switch (direction) {
      case 'above':
        // Wall at (x, y+1) to (x+1, y+1) - matches wallSystem.isOccupied(x, y + 1, x + 1, y + 1)
        doorCoords = { startX: x, startY: y + 1, endX: x + 1, endY: y + 1 };
        break;
      case 'below':
        // Wall at (x, y) to (x+1, y) - matches wallSystem.isOccupied(x, y, x + 1, y)
        doorCoords = { startX: x, startY: y, endX: x + 1, endY: y };
        break;
      case 'left':
        // Wall at (x, y) to (x, y+1) - matches wallSystem.isOccupied(x, y, x, y + 1)
        doorCoords = { startX: x, startY: y, endX: x, endY: y + 1 };
        break;
      case 'right':
        // Wall at (x+1, y) to (x+1, y+1) - matches wallSystem.isOccupied(x + 1, y, x + 1, y + 1)
        doorCoords = { startX: x + 1, startY: y, endX: x + 1, endY: y + 1 };
        break;
    }
    
    // Convert door coordinates to world coordinates
    const startWorld = this.gridSystem.gridToWorld(doorCoords.startX, doorCoords.startY);
    const endWorld = this.gridSystem.gridToWorld(doorCoords.endX, doorCoords.endY);
    
    // Create graphics for door preview
    const graphic = new Graphics();
    graphic.lineStyle(4, 0x8B4513, 1); // Thick brown line for door preview
    graphic.moveTo(0, 0);
    graphic.lineTo(endWorld.x - startWorld.x, endWorld.y - startWorld.y);
    graphic.position.copyFrom(startWorld);
    
    this.previewLayer.addChild(graphic);
  }

  private getRotatedDimensions(definition: any, rotation: FurnitureRotation): { width: number, height: number } {
    // At 90° and 270°, width and height are swapped
    if (rotation === 90 || rotation === 270) {
      return { width: definition.gridHeight, height: definition.gridWidth };
    }
    return { width: definition.gridWidth, height: definition.gridHeight };
  }

  private evaluateFloorArea(start: Point, end: Point): {
    validTiles: Point[];
    invalidTiles: Point[];
  } {
    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    const minY = Math.min(start.y, end.y);
    const maxY = Math.max(start.y, end.y);

    const validTiles: Point[] = [];
    const invalidTiles: Point[] = [];

    for (let x = minX; x <= maxX; x += 1) {
      for (let y = minY; y <= maxY; y += 1) {
        if (this.gridSystem.isOccupied(x, y, this.currentFloor)) {
          invalidTiles.push(new Point(x, y));
        } else {
          validTiles.push(new Point(x, y));
        }
      }
    }

    return { validTiles, invalidTiles };
  }

  private evaluateRoomCategoryArea(start: Point, end: Point): {
    validTiles: Point[];
    invalidTiles: Point[];
  } {
    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    const minY = Math.min(start.y, end.y);
    const maxY = Math.max(start.y, end.y);

    const validTiles: Point[] = [];
    const invalidTiles: Point[] = [];

    for (let x = minX; x <= maxX; x += 1) {
      for (let y = minY; y <= maxY; y += 1) {
        // Only allow categorization on existing floor tiles
        if (this.gridSystem.hasFloor(x, y, this.currentFloor)) {
          validTiles.push(new Point(x, y));
        } else {
          invalidTiles.push(new Point(x, y));
        }
      }
    }

    return { validTiles, invalidTiles };
  }

  private evaluateWallEdges(start: Point, end: Point): {
    validEdges: WallEdge[];
    invalidEdges: WallEdge[];
  } {
    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    const minY = Math.min(start.y, end.y);
    const maxY = Math.max(start.y, end.y);

    const selectedTiles = new Set<string>();
    for (let x = minX; x <= maxX; x += 1) {
      for (let y = minY; y <= maxY; y += 1) {
        selectedTiles.add(this.getTileKey(x, y));
      }
    }

    const edgeMap = new Map<string, WallEdge>();
    const directions = [
      { dx: 0, dy: -1 },
      { dx: 0, dy: 1 },
      { dx: -1, dy: 0 },
      { dx: 1, dy: 0 }
    ];

    for (let x = minX; x <= maxX; x += 1) {
      for (let y = minY; y <= maxY; y += 1) {
        const tileKey = this.getTileKey(x, y);
        if (!selectedTiles.has(tileKey)) {
          continue;
        }

        directions.forEach(({ dx, dy }) => {
          const neighborX = x + dx;
          const neighborY = y + dy;
          const neighborKey = this.getTileKey(neighborX, neighborY);

          if (selectedTiles.has(neighborKey)) {
            return;
          }

          const direction: WallDirection = dx !== 0 ? 'vertical' : 'horizontal';
          const edgeKey = this.getWallEdgeKey(x, y, neighborX, neighborY);

          if (!edgeMap.has(edgeKey)) {
            edgeMap.set(edgeKey, {
              tile: new Point(x, y),
              neighbor: new Point(neighborX, neighborY),
              direction
            });
          }
        });
      }
    }

    const validEdges: WallEdge[] = [];
    const invalidEdges: WallEdge[] = [];

    edgeMap.forEach((edge) => {
      const canPlace = this.wallSystem.canPlace(
        edge.tile.x,
        edge.tile.y,
        edge.neighbor.x,
        edge.neighbor.y,
        this.currentFloor
      );

      if (canPlace) {
        validEdges.push(edge);
      } else {
        invalidEdges.push(edge);
      }
    });

    return { validEdges, invalidEdges };
  }

  private addWallPreview(edge: WallEdge, isValid: boolean): void {
    const { start, end } = this.computeWallWorldEdge(edge);
    const preview = new Graphics();
    preview.lineStyle(6, isValid ? 0x7ad37a : 0xd37a7a, 0.9);
    preview.moveTo(0, 0);
    preview.lineTo(end.x - start.x, end.y - start.y);
    preview.position.copyFrom(start);
    this.previewLayer.addChild(preview);
  }

  public placeFloor(tilePosition: Point, floor = this.currentFloor): GridObject {
    const gridObject: GridObject = {
      id: `floor-${tilePosition.x}-${tilePosition.y}-${floor}`,
      type: 'floor',
      gridX: tilePosition.x,
      gridY: tilePosition.y,
      floor,
      width: 1,
      height: 1
    };

    this.gridSystem.setObject(gridObject);

    const sprite = new Sprite(this.createFloorTexture());
    sprite.anchor.set(0.5, 1);
    sprite.position.copyFrom(this.gridSystem.gridToWorld(tilePosition.x, tilePosition.y));
    sprite.name = gridObject.id;

    this.layerSystem.add(sprite, 'Ground', tilePosition);
    gridObject.sprite = sprite;

    return gridObject;
  }

  private placeWall(edge: WallEdge): WallSegment {
    const segment = this.wallSystem.addSegment(
      edge.tile.x,
      edge.tile.y,
      edge.neighbor.x,
      edge.neighbor.y,
      this.currentFloor
    );

    const { start, end } = this.computeWallWorldEdge(edge);
  const wall = new Graphics();
  wall.lineStyle(8, 0xc9c3b5, 1);
    wall.moveTo(0, 0);
    wall.lineTo(end.x - start.x, end.y - start.y);
    wall.position.copyFrom(start);

    const depthPoint = new Point(
      Math.max(edge.tile.x, edge.neighbor.x),
      Math.max(edge.tile.y, edge.neighbor.y)
    );

    this.layerSystem.add(wall, 'Walls', depthPoint);
    segment.graphic = wall;
    return segment;
  }

  private placeSingleLineWall(start: Point, end: Point): WallSegment | null {
    // For single line walls, constrain to cardinal directions only
    const constrainedEnd = this.constrainToCardinalDirection(start, end);
    
    // Use the special single line method that bypasses adjacency checks
    const segment = this.wallSystem.addSingleLineSegment(
      start.x,
      start.y,
      constrainedEnd.x,
      constrainedEnd.y,
      this.currentFloor
    );

    // Create the wall graphic
    const startWorld = this.gridSystem.gridToWorld(start.x, start.y);
    const endWorld = this.gridSystem.gridToWorld(constrainedEnd.x, constrainedEnd.y);
    
    const wall = new Graphics();
    wall.lineStyle(8, 0xc9c3b5, 1);
    wall.moveTo(0, 0);
    wall.lineTo(endWorld.x - startWorld.x, endWorld.y - startWorld.y);
    wall.position.copyFrom(startWorld);

    const depthPoint = new Point(
      Math.max(start.x, constrainedEnd.x),
      Math.max(start.y, constrainedEnd.y)
    );

    this.layerSystem.add(wall, 'Walls', depthPoint);
    segment.graphic = wall;
    return segment;
  }

  private clearPreview(): void {
    this.previewLayer.removeChildren().forEach((child) => child.destroy());
  }

  private createFloorPreviewTexture(color: number, alpha: number): Texture {
    const diamond = new Graphics()
      .beginFill(color, alpha)
      .drawPolygon([
        0,
        GridSystem.TILE_HEIGHT / 2,
        GridSystem.TILE_WIDTH / 2,
        0,
        GridSystem.TILE_WIDTH,
        GridSystem.TILE_HEIGHT / 2,
        GridSystem.TILE_WIDTH / 2,
        GridSystem.TILE_HEIGHT
      ])
      .endFill();

    const texture = this.app.renderer.generateTexture(diamond);
    diamond.destroy();
    return texture;
  }

  private createRoomCategoryPreviewTexture(): Texture {
    // Create a diamond shape for room category previews (will be tinted per category)
    const diamond = new Graphics()
      .beginFill(0xffffff, 0.7) // White base - will be tinted
      .drawPolygon([
        0,
        GridSystem.TILE_HEIGHT / 2,
        GridSystem.TILE_WIDTH / 2,
        0,
        GridSystem.TILE_WIDTH,
        GridSystem.TILE_HEIGHT / 2,
        GridSystem.TILE_WIDTH / 2,
        GridSystem.TILE_HEIGHT
      ])
      .endFill();

    const texture = this.app.renderer.generateTexture(diamond);
    diamond.destroy();
    return texture;
  }

  private createFloorTexture(): Texture {
    const graphic = new Graphics()
      .lineStyle(2, 0xb7a57a, 0.9)
      .beginFill(0xe8d8a0, 1)
      .drawPolygon([
        0,
        GridSystem.TILE_HEIGHT / 2,
        GridSystem.TILE_WIDTH / 2,
        0,
        GridSystem.TILE_WIDTH,
        GridSystem.TILE_HEIGHT / 2,
        GridSystem.TILE_WIDTH / 2,
        GridSystem.TILE_HEIGHT
      ])
      .endFill();

    const texture = this.app.renderer.generateTexture(graphic);
    graphic.destroy();
    return texture;
  }

  private computeWallWorldEdge(edge: WallEdge): { start: Point; end: Point } {
    const corners = this.getTileCorners(edge.tile.x, edge.tile.y);

    if (edge.direction === 'horizontal') {
      if (edge.neighbor.y > edge.tile.y) {
        return {
          start: corners.bottom,
          end: corners.left
        };
      }

      return {
        start: corners.top,
        end: corners.right
      };
    }

    if (edge.neighbor.x > edge.tile.x) {
      return {
        start: corners.right,
        end: corners.bottom
      };
    }

    return {
      start: corners.left,
      end: corners.top
    };
  }

  private constrainToCardinalDirection(start: Point, end: Point): Point {
    // Calculate the difference in X and Y
    const deltaX = end.x - start.x;
    const deltaY = end.y - start.y;
    
    // If no movement, return the start point
    if (deltaX === 0 && deltaY === 0) {
      return new Point(start.x, start.y);
    }
    
    // Determine which direction has the larger movement
    if (Math.abs(deltaX) >= Math.abs(deltaY)) {
      // Horizontal movement is dominant - constrain to horizontal line
      return new Point(end.x, start.y);
    } else {
      // Vertical movement is dominant - constrain to vertical line
      return new Point(start.x, end.y);
    }
  }

  private segmentToEdge(segment: WallSegment): WallEdge {
    return {
      tile: new Point(segment.end.x, segment.end.y),
      neighbor: new Point(segment.start.x, segment.start.y),
      direction: segment.direction
    };
  }

  private collectWallCandidates(gridPoint: Point, floor: number): Array<{
    tile: Point;
    neighbor: Point;
    worldStart: Point;
    worldEnd: Point;
  }> {
    const bases = [
      new Point(gridPoint.x, gridPoint.y),
      new Point(gridPoint.x - 1, gridPoint.y),
      new Point(gridPoint.x + 1, gridPoint.y),
      new Point(gridPoint.x, gridPoint.y - 1),
      new Point(gridPoint.x, gridPoint.y + 1)
    ];

    const candidates: Array<{
      tile: Point;
      neighbor: Point;
      worldStart: Point;
      worldEnd: Point;
    }> = [];
    const seen = new Set<string>();

    bases.forEach((base) => {
      const directions = [
        { dx: 1, dy: 0 },
        { dx: -1, dy: 0 },
        { dx: 0, dy: 1 },
        { dx: 0, dy: -1 }
      ];

      directions.forEach(({ dx, dy }) => {
        const neighbor = new Point(base.x + dx, base.y + dy);
        const segment = this.wallSystem.getSegment(base.x, base.y, neighbor.x, neighbor.y, floor);
        if (!segment) {
          return;
        }

        const key = this.getWallEdgeKey(base.x, base.y, neighbor.x, neighbor.y);
        if (seen.has(key)) {
          return;
        }
        seen.add(key);

        const { start, end } = this.computeWallWorldEdge({
          tile: new Point(base.x, base.y),
          neighbor: new Point(neighbor.x, neighbor.y),
          direction: segment.direction
        });

        candidates.push({
          tile: new Point(base.x, base.y),
          neighbor: new Point(neighbor.x, neighbor.y),
          worldStart: start,
          worldEnd: end
        });
      });
    });

    return candidates;
  }

  private pointSegmentDistance(point: Point, start: Point, end: Point): number {
    const vx = end.x - start.x;
    const vy = end.y - start.y;
    const wx = point.x - start.x;
    const wy = point.y - start.y;

    const segmentLengthSquared = vx * vx + vy * vy;
    if (segmentLengthSquared === 0) {
      return Math.hypot(point.x - start.x, point.y - start.y);
    }

    const t = Math.max(0, Math.min(1, (wx * vx + wy * vy) / segmentLengthSquared));
    const projX = start.x + t * vx;
    const projY = start.y + t * vy;
    return Math.hypot(point.x - projX, point.y - projY);
  }

  private getTileCorners(gridX: number, gridY: number): {
    top: Point;
    right: Point;
    bottom: Point;
    left: Point;
  } {
    const base = this.gridSystem.gridToWorld(gridX, gridY);
    return {
      top: new Point(base.x, base.y - GridSystem.TILE_HEIGHT),
      right: new Point(
        base.x + GridSystem.TILE_WIDTH / 2,
        base.y - GridSystem.TILE_HEIGHT / 2
      ),
      bottom: new Point(base.x, base.y),
      left: new Point(
        base.x - GridSystem.TILE_WIDTH / 2,
        base.y - GridSystem.TILE_HEIGHT / 2
      )
    };
  }

  private getTileKey(x: number, y: number): string {
    return `${x},${y}`;
  }

  private getWallEdgeKey(
    tileX: number,
    tileY: number,
    neighborX: number,
    neighborY: number
  ): string {
    const startX = Math.min(tileX, neighborX);
    const startY = Math.min(tileY, neighborY);
    const endX = Math.max(tileX, neighborX);
    const endY = Math.max(tileY, neighborY);
    const direction = tileX === neighborX ? 'h' : 'v';
    return `${direction}:${startX},${startY}:${endX},${endY}`;
  }
}
